import React, { useState, useEffect } from 'react';
import { careers } from '../mockData';
import { MapPin, Clock, Briefcase, ArrowRight, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Careers = () => {
  const { toast } = useToast();
  const [selectedJob, setSelectedJob] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    position: '',
    coverLetter: '',
    resume: null
  });

  useEffect(() => {
    fetchJobs();
  }, []);

  const fetchJobs = async () => {
    try {
      const response = await axios.get(`${API}/jobs`);
      setJobs(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching jobs:', error);
      setJobs(careers); // Fallback to mock data
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    setFormData(prev => ({ ...prev, resume: e.target.files[0] }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    const submitData = new FormData();
    submitData.append('fullName', formData.fullName);
    submitData.append('email', formData.email);
    submitData.append('phone', formData.phone);
    submitData.append('position', formData.position);
    submitData.append('coverLetter', formData.coverLetter);
    submitData.append('resume', formData.resume);

    try {
      await axios.post(`${API}/careers/apply`, submitData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      toast({
        title: "Application Submitted!",
        description: "Thank you for your interest. We'll review your application and get back to you soon.",
        duration: 5000
      });
      
      setFormData({
        fullName: '',
        email: '',
        phone: '',
        position: '',
        coverLetter: '',
        resume: null
      });
      setSelectedJob(null);
      // Reset file input
      document.querySelector('input[type="file"]').value = '';
    } catch (error) {
      console.error('Error submitting application:', error);
      toast({
        title: "Submission Failed",
        description: error.response?.data?.detail || "There was an error submitting your application. Please try again.",
        variant: "destructive",
        duration: 5000
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white pt-20">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-blue-50 to-emerald-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              Join Our Team
            </h1>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
              Help us transform traditional recipes into successful food brands
            </p>
          </div>
        </div>
      </section>

      {/* Why Work With Us */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Why Join Fresh Bites Group?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Be part of a growing company that values tradition, innovation, and excellence
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-white p-8 rounded-none shadow-lg hover:shadow-xl transition-all duration-300 border-t-4 border-emerald-600">
              <h3 className="text-xl font-bold text-gray-900 mb-3">Growth Opportunities</h3>
              <p className="text-gray-600">
                Expand your skills in a fast-growing food manufacturing company
              </p>
            </div>

            <div className="bg-white p-8 rounded-none shadow-lg hover:shadow-xl transition-all duration-300 border-t-4 border-blue-600">
              <h3 className="text-xl font-bold text-gray-900 mb-3">Cultural Diversity</h3>
              <p className="text-gray-600">
                Work with authentic cuisines from around the world, especially Indian/Telugu
              </p>
            </div>

            <div className="bg-white p-8 rounded-none shadow-lg hover:shadow-xl transition-all duration-300 border-t-4 border-emerald-600">
              <h3 className="text-xl font-bold text-gray-900 mb-3">Innovation Focus</h3>
              <p className="text-gray-600">
                Combine traditional methods with modern food technology
              </p>
            </div>

            <div className="bg-white p-8 rounded-none shadow-lg hover:shadow-xl transition-all duration-300 border-t-4 border-blue-600">
              <h3 className="text-xl font-bold text-gray-900 mb-3">Competitive Benefits</h3>
              <p className="text-gray-600">
                Attractive salary, professional development, and work-life balance
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Open Positions */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Open Positions
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Explore exciting opportunities across our operations
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {loading ? (
              <div className="col-span-2 text-center py-12">
                <p className="text-gray-600">Loading positions...</p>
              </div>
            ) : jobs.length === 0 ? (
              <div className="col-span-2 text-center py-12">
                <p className="text-gray-600">No open positions at the moment. Check back soon!</p>
              </div>
            ) : (
              jobs.map((job) => (
              <div key={job.id} className="bg-white rounded-none shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300">
                <img 
                  src={job.image}
                  alt={job.title}
                  className="w-full h-64 object-cover"
                />
                <div className="p-8">
                  <div className="bg-emerald-600 text-white text-xs font-bold px-3 py-1 rounded-none inline-block mb-4">
                    {job.department}
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">{job.title}</h3>
                  
                  <div className="flex flex-wrap gap-4 mb-6 text-gray-600">
                    <div className="flex items-center gap-2">
                      <MapPin size={18} className="text-emerald-600" />
                      <span>{job.location}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock size={18} className="text-emerald-600" />
                      <span>{job.type}</span>
                    </div>
                  </div>

                  <p className="text-gray-700 mb-6">{job.description}</p>

                  <div className="mb-6">
                    <h4 className="font-bold text-gray-900 mb-3">Key Requirements:</h4>
                    <ul className="space-y-2">
                      {job.requirements.map((req, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-gray-700">
                          <CheckCircle size={18} className="text-emerald-600 flex-shrink-0 mt-0.5" />
                          <span>{req}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <Button
                    onClick={() => {
                      setSelectedJob(job);
                      setFormData(prev => ({ ...prev, position: job.title }));
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                    className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-6 rounded-none font-semibold text-lg transition-all duration-300"
                  >
                    Apply Now
                  </Button>
                </div>
              </div>
            ))
            )}
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section className="py-20">
        <div className="max-w-3xl mx-auto px-6">
          <div className="bg-white rounded-none shadow-xl p-8 md:p-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-6 text-center">
              {selectedJob ? `Apply for ${selectedJob.title}` : 'Submit Your Application'}
            </h2>
            <p className="text-gray-600 mb-8 text-center">
              Fill out the form below and we'll get back to you within 3-5 business days
            </p>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-gray-700 font-semibold mb-2">Full Name *</label>
                <Input
                  type="text"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleInputChange}
                  required
                  className="w-full rounded-none border-gray-300"
                  placeholder="John Doe"
                />
              </div>

              <div>
                <label className="block text-gray-700 font-semibold mb-2">Email Address *</label>
                <Input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="w-full rounded-none border-gray-300"
                  placeholder="john@example.com"
                />
              </div>

              <div>
                <label className="block text-gray-700 font-semibold mb-2">Phone Number *</label>
                <Input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  required
                  className="w-full rounded-none border-gray-300"
                  placeholder="+44 7XXX XXXXXX"
                />
              </div>

              <div>
                <label className="block text-gray-700 font-semibold mb-2">Position Applied For *</label>
                <Input
                  type="text"
                  name="position"
                  value={formData.position}
                  onChange={handleInputChange}
                  required
                  className="w-full rounded-none border-gray-300"
                  placeholder="Development Chef"
                />
              </div>

              <div>
                <label className="block text-gray-700 font-semibold mb-2">Cover Letter *</label>
                <Textarea
                  name="coverLetter"
                  value={formData.coverLetter}
                  onChange={handleInputChange}
                  required
                  rows={6}
                  className="w-full rounded-none border-gray-300"
                  placeholder="Tell us why you're interested in joining Fresh Bites Group..."
                />
              </div>

              <div>
                <label className="block text-gray-700 font-semibold mb-2">Resume/CV *</label>
                <Input
                  type="file"
                  onChange={handleFileChange}
                  accept=".pdf,.doc,.docx"
                  required
                  className="w-full rounded-none border-gray-300"
                />
                <p className="text-sm text-gray-500 mt-2">Accepted formats: PDF, DOC, DOCX (Max 5MB)</p>
              </div>

              <Button
                type="submit"
                disabled={submitting}
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-6 rounded-none font-bold text-lg transition-all duration-300"
              >
                {submitting ? 'Submitting...' : 'Submit Application'}
              </Button>
            </form>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-blue-600">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Don't See the Right Position?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
            We're always looking for talented individuals. Send us your CV and we'll keep you in mind for future opportunities.
          </p>
          <a
            href="mailto:are@freshbitesgroup.com"
            className="inline-block bg-white text-blue-600 px-10 py-5 rounded-none font-bold text-lg transition-all duration-300 hover:bg-gray-100 hover:shadow-2xl hover:-translate-y-1"
          >
            Email Your CV
          </a>
        </div>
      </section>
    </div>
  );
};

export default Careers;
